import * as React from "react"

import {
  useActiveBranchSlug,
  useActiveWorkspaceSlug,
  useShowMultiBranchWorkspaceUi,
  useTenantAuthorization,
} from "@/features/auth"
import {
  TenantAuthorizationAction,
  TenantAuthorizationDecisionStatus,
} from "@/features/auth/tenant-authorization/model/tenant-authorization.types"
import { useTenantAuthFlow } from "@/features/auth/providers/tenant-auth-flow-provider"
import { resolveBranchIdForWorkspaceUserAccess } from "@/features/auth/lib/workspace-user-access"
import { useNavCapabilityFilter } from "@/features/dashboard/hooks/use-nav-capability-filter"
import {
  CHURCH_NAV_ITEMS,
  filterVisibleChurchNavSections,
  resolveWorkspaceAccessRecord,
  type ChurchNavSection,
} from "@/features/dashboard/model/church-nav"
import { filterParishNavSections, PARISH_NAV_ITEMS } from "@/features/dashboard/model/parish-nav"
import { isParishBranchAlias } from "@/shared/lib/branch/branch-admin-routing"
import { useProductFeatureBadgeOverlay } from "@/features/dashboard/hooks/use-product-feature-badges"

export type TenantNavSections = {
  /** The sections for the CURRENT context, filtered for permissions and capabilities. */
  sections: ChurchNavSection[]
  /** True while the authorization snapshot is still settling; consumers should show loading, not empty nav. */
  isResolving: boolean
  /** True when this is the parish/organization context, which has its own curated nav. */
  isParish: boolean
  branchSlug: string
  workspaceSlug: string
  branchId: string | null
}

/**
 * The one answer to "which nav does this context have?", shared by the sidebar and the header breadcrumb.
 *
 * It existed only inside `AppSidebar` before, and the breadcrumb re-derived a subset of it — no parish
 * branch, no mono rule — so on every organization route the two disagreed about what the user was looking at:
 * the sidebar highlighted "Ministères" while the breadcrumb, reading the BRANCH nav, found no match and
 * rendered nothing at all. One resolver, two consumers.
 */
export function useTenantNavSections(): TenantNavSections {
  const branchSlug = useActiveBranchSlug() ?? ""
  const workspaceSlug = useActiveWorkspaceSlug() ?? ""
  // `false` for SINGLE_BRANCH ("mono") workspaces, where the single branch IS the workspace.
  const showMultiBranchUi = useShowMultiBranchWorkspaceUi()
  // A MONO workspace routes branch-less on the parish alias but must show the normal BRANCH nav (it IS the
  // branch), so the parish nav only applies to multi-branch workspaces (ADR-0025).
  const isParish = isParishBranchAlias(branchSlug) && showMultiBranchUi
  const { authMe } = useTenantAuthFlow()
  const navCapabilityFilter = useNavCapabilityFilter()
  const authorization = useTenantAuthorization()
  // A resolving snapshot is not a denial. The old `can()` adapter returns false for both, which made every
  // permission-gated sidebar entry disappear for a render during auth refreshes.
  const authorizationIsResolving =
    authorization.routeDecision(TenantAuthorizationAction.DashboardView).status ===
    TenantAuthorizationDecisionStatus.Resolving

  const currentAccess = React.useMemo(
    () => resolveWorkspaceAccessRecord(authMe, workspaceSlug),
    [authMe, workspaceSlug],
  )
  const branchId = React.useMemo(
    () => resolveBranchIdForWorkspaceUserAccess(currentAccess, branchSlug),
    [currentAccess, branchSlug],
  )

  const resolvedSections = React.useMemo(() => {
    // Parish nav must run through the same capability filter + finance-setup gate as branch nav: until the
    // parish's minimum finance setup is done, its Finance section collapses to the single "Configure" entry
    // (not the full submenu list) — identical to the branch view, in every scope.
    if (isParish)
      return filterParishNavSections(navCapabilityFilter(PARISH_NAV_ITEMS), {
        authorization,
        showMultiBranchWorkspaceUi: showMultiBranchUi,
      })
    const branchSections = navCapabilityFilter(
      filterVisibleChurchNavSections(CHURCH_NAV_ITEMS, {
        authMe,
        activeWorkspaceSlug: workspaceSlug,
        activeWorkspaceBranchId: branchId,
        showMultiBranchWorkspaceUi: showMultiBranchUi,
      }),
    )
    // Mono ("single branch") workspaces ARE the branch — drop the redundant "My Branch" self-service section;
    // branch management lives under Settings instead.
    return showMultiBranchUi
      ? branchSections
      : branchSections.filter((section) => section.url !== "/my-branch")
  }, [
    authMe,
    branchId,
    authorization,
    isParish,
    navCapabilityFilter,
    showMultiBranchUi,
    workspaceSlug,
  ])

  /*
   * Keep the last *settled* navigation only while the same workspace/context is being refreshed. We never
   * carry it to another branch or workspace, and once authorization resolves the registry result replaces it
   * immediately. This avoids an empty sidebar without turning a pending decision into a permission grant.
   */
  const navigationContextKey = `${workspaceSlug}:${branchSlug}:${isParish ? "parish" : "branch"}`
  const lastSettledNavigation = React.useRef<{ key: string; sections: ChurchNavSection[] } | null>(null)
  if (!authorizationIsResolving) {
    lastSettledNavigation.current = { key: navigationContextKey, sections: resolvedSections }
  }
  const sections =
    authorizationIsResolving && lastSettledNavigation.current?.key === navigationContextKey
      ? lastSettledNavigation.current.sections
      : resolvedSections

  /*
   * "New" and "Beta" arrive from the server, on top of the curated sections.
   *
   * Applied here rather than in the sidebar because this hook is the single answer to "which nav does this
   * context have?" — the header breadcrumb reads it too, and a badge that only the sidebar knew about would
   * be the same kind of drift this hook was written to end.
   */
  const badgedSections = useProductFeatureBadgeOverlay(sections)

  return { sections: badgedSections, isResolving: authorizationIsResolving, isParish, branchSlug, workspaceSlug, branchId }
}
